import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import Swal from 'sweetalert2';

let renderCount = 0;

const Login = () => {
  renderCount++;
  console.log('🔄 Rendu #', renderCount, ' du composant Login');
  
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (loginError) {
      console.log('Affichage du SweetAlert avec erreur:', loginError);
      
      const swalInstance = Swal.fire({
        icon: 'error',
        title: 'Échec de connexion',
        html: `<p style="font-size: 16px; color: var(--swal2-html-color, #333);">${loginError}</p>`,
        confirmButtonText: 'Réessayer',
        confirmButtonColor: '#4F8CD6',
        buttonsStyling: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        width: '400px',
        padding: '2rem',
        heightAuto: false,
        position: 'center',
        customClass: {
          container: 'swal2-container-custom',
          popup: 'swal2-popup-custom dark:bg-gray-800 dark:text-gray-200'
        },
        background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#fff',
        color: document.documentElement.classList.contains('dark') ? '#e5e7eb' : '#111827',
        didOpen: () => {
          console.log('✅ SweetAlert OUVERT - visible à l\'écran');
          // Force le centrage et le z-index
          const container = document.querySelector('.swal2-container');
          if (container) {
            container.style.zIndex = '999999';
            container.style.display = 'flex';
            container.style.alignItems = 'center';
            container.style.justifyContent = 'center';
            console.log('Container SweetAlert centré:', container);
          }
        },
        didClose: () => {
          console.log('❌ SweetAlert FERMÉ');
          setLoginError(null);
        }
      });
      
      console.log('Instance SweetAlert:', swalInstance);
    }
  }, [loginError]);

  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/app/dashboard" replace />;

  const onSubmit = async (data) => {
    console.log('=== DÉBUT onSubmit ===');
    setFormLoading(true);
    
    const result = await login(data);
    console.log('Résultat login:', result);
    
    setFormLoading(false);
    
    if (result.success) {
      console.log('Connexion réussie, navigation vers dashboard');
      toast.success('Connexion réussie!');
      navigate('/app/dashboard', { replace: true });
    } else {
      console.log('Connexion échouée, affichage erreur:', result.error);
      // Définir l'erreur dans le state pour déclencher le useEffect
      setLoginError(result.error || 'Nom d\'utilisateur ou mot de passe incorrect');
      console.log('loginError state défini');
    }
    console.log('=== FIN onSubmit ===');
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-200 dark:from-gray-900 dark:to-gray-800">
      <div
        className="flex w-full max-w-5xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700"
        style={{ minHeight: 500 }}
      >
        {/* Left: Illustration image */}
        <div className="hidden md:flex w-1/2 items-center justify-center bg-gray-100 dark:bg-gray-700">
          <img
            src="/images/schola2.png"
            alt="Illustration gestion scolaire Schola"
            className="object-contain shadow-md"
            style={{ maxHeight: '100%', width: '100%' }}
            onError={e => e.target.style.opacity = 0}
          />
        </div>
        {/* Right: Form */}
        <div className="flex w-full md:w-1/2 justify-center items-center bg-white dark:bg-gray-800 p-6">
          <div className="max-w-sm w-full space-y-8 mx-auto">
            {/* Logo sans déformation */}
            <div className="flex justify-center">
              <img
                src={process.env.PUBLIC_URL + '/logo_schola.jpg'}
                alt="Logo Schola"
                className="mx-auto mb-2 animate-bounce"
                style={{ height: 80, width: 230, maxWidth: 250, animationDuration: '1.4s', borderRadius: 16 }}
              />
            </div>
            {/* Texte de bienvenue */}
            <div className="text-center mb-6">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-200 leading-tight">
                Bienvenue&nbsp;à&nbsp;Schola
              </div>
              <div className="mt-1 text-base text-primary-600 dark:text-primary-400">
                Gestion&nbsp;d'établissements&nbsp;scolaires
              </div>
            </div>
            {/* Formulaire modernisé */}
            <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
              <div className="space-y-4">
                {/* Utilisateur */}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-400">
                    <FaUser size={18} />
                  </span>
                  <input
                    {...register('username', { required: 'Nom d\'utilisateur requis' })}
                    type="text"
                    autoComplete="username"
                    className="pl-10 pr-3 py-2 w-full rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-800 text-gray-900 dark:text-gray-200 shadow-sm transition placeholder-gray-400 dark:placeholder-gray-400 sm:text-sm"
                    placeholder="Nom d'utilisateur"
                  />
                  {errors.username && (
                    <p className="mt-1 text-xs text-red-600">{errors.username.message}</p>
                  )}
                </div>
                {/* Mot de passe */}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-400">
                    <FaLock size={18} />
                  </span>
                  <input
                    {...register('password', { required: 'Mot de passe requis' })}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className="pl-10 pr-10 py-2 w-full rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-800 text-gray-900 dark:text-gray-200 shadow-sm transition placeholder-gray-400 dark:placeholder-gray-400 sm:text-sm"
                    placeholder="Mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                  >
                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                  )}
                </div>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={formLoading}
                  className="group relative w-full flex justify-center py-2 px-4 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition"
                >
                  {formLoading ? 'Connexion...' : 'Se connecter'}
                </button>
              </div>
            </form>
            {/* Copyright du footer */}
            <div className="mt-8 flex flex-col items-center justify-center">
              <img
                src={process.env.PUBLIC_URL + '/tpsoft_copyright.png'}
                alt="TPSoft Logo Copyright"
                style={{ height: 48, width: 'auto', maxWidth: 120, borderRadius: 8, objectFit: 'contain' }}
                className="mb-2 opacity-80"
                onError={e => e.target.style.opacity = 0}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                TPSoft Informatique - Juin 2025 - Tous droits réservés.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
